import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Box, Button, Card, CardContent, CircularProgress, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CandidateAggregateSearch,
  ICandidateAggregateSearch,
  initialCandidateAggregateSearch,
} from 'app/ComponentLibrary/Filter/CandidateSearch';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridColumns as XGridColumns,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { ChipsContainer } from 'app/ComponentLibrary/Filter/ChipsContainer';
import { TypeAheadDropdown } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/TypeAheadDropdown';
import { ISelection } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/CustomTypeAhead';
import { FilterPreferenceSelector } from 'app/components/GlobalSearch/FilterSpecComponents/Actions/FilterPreferenceSelector';
import { SaveAsModal } from 'app/components/GlobalSearch/FilterSpecComponents/Actions/SaveAsModal';
import { SaveModal } from 'app/components/GlobalSearch/FilterSpecComponents/Actions/SaveModal';
import { getMultiSelectOptions } from 'app/components/GlobalSearch/helper';
import { CandidateService } from 'app/services/CandidateServices/candidate-service';
import { httpSuccess } from 'app/services/serviceHelpers';
import { navigationStickActions } from 'app/components/GlobalNavigationMenu/NavigationStick.redux';
import { getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import { DetailsPage } from 'app/layout/pages/DetailsPage';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import _ from 'lodash';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreference, selectUserPreferenceLoaded } from 'oidc/UserDevicePreference/userPreference.selectors';
import { RootState } from 'types';
import { DuplicateFilterAlertModal } from 'app/components/GlobalSearch/FilterSpecComponents/Actions/DuplicateFilterAlertModal';
import { SessionKey, StorageType, getSessionValue, removeSessionValue, setSessionValue } from 'utils/customHooks/sessionStorage/sessionHelpers';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { selectColumnVisibilityModel } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import { trackException } from 'app-insights/appInsightsTracking';
import { navigationProfilesAction } from '@AMIEWEB/GlobalSearch/store/NavigationProfiles.redux';

import {
  formatEngagementGridCell,
  formatIsoToUsDate,
  IInquiryRecord,
} from './candidateLeadsUtils';
import {
  CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
  CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG,
  CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY,
} from './candidateInquiryGridSurfaceIds';
import {
  buildSnapshotFromApplied,
  checkEngagementFiltersApplied,
  entriesToAppliedState,
  normalizedSnapshotJson,
  snapshotToEntries,
  type CandidateEngagementTabKey,
} from './candidateEngagementFilterViews';

interface IBrandOption {
  id?: number;
  name?: string;
  value?: string;
}

const useStyles = makeStyles()(() => ({
  root: {
    padding: 0,
  },
  viewActionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 0,
  },
  viewActionsLabel: {
    color: 'rgba(0, 0, 0, 0.87)',
  },
  viewButton: {
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: '20px',
    '&:hover': {
      backgroundColor: '#003E68',
    },
  },
  statsContainer: {
    marginTop: 8,
    marginBottom: 12,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 12,
    '@media (max-width: 1100px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  statCard: {
    border: '1px solid #E8E8E8',
    boxShadow: 'none',
    borderRadius: 4,
    height: '100%',
  },
  statIconBox: {
    width: 52,
    height: 52,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: 12,
  },
  statCardIconContent: {
    display: 'flex',
    alignItems: 'center',
    '&:last-child': {
      paddingBottom: 16,
    },
  },
  statCardCandidatesContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 10,
    '&:last-child': {
      paddingBottom: 10,
    },
  },
  statCandidatesIcon: {
    color: '#5E56B4',
    fontSize: 24,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 12,
    color: '#656565',
    lineHeight: '16px',
  },
  statValue: {
    fontSize: 28,
    lineHeight: '30px',
    fontWeight: 500,
    color: '#303030',
  },
  filtersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr)) auto',
    gap: 8,
    alignItems: 'end',
    padding: '12px 12px 0 12px',
    marginBottom: 4,
  },
  filterCell: {
    minWidth: 0,
  },
  /** Align with TypeAheadDropdown / placement search filter triggers (outline, height, type scale). */
  leadOriginField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      minHeight: 36.7,
      fontSize: 12,
      fontWeight: 400,
      borderRadius: 4,
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 10px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  },
  filterActionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexShrink: 0,
    justifySelf: 'end',
  },
  chipsRow: {
    display: 'block',
    padding: '4px 12px 0',
    marginBottom: 10,
  },
  gridSection: {
    marginTop: 12,
     border: '1px solid #ececec',
    borderRadius: '0px',
    backgroundColor: '#fff',
     boxShadow: '0 0 0 1px #ececec inset',
  },
  tableWrap: {
    padding: '0 12px 12px 12px',
  },
  tabRoot: {
    marginTop: 14,
    minHeight: 38,
  },
  tabCell: {
    minHeight: 38,
    textTransform: 'none',
    fontSize: 12,
    paddingLeft: 14,
    paddingRight: 14,
  },
}));

export const CandidateEngagementInquiries = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const candidateService = React.useMemo(() => CandidateService.getInstance(), []);

  const filterPreferences = useSelector((state: RootState) =>
    state.userPreferenceData?.userGridPreferences?.find(
      item => item.id === CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
    ),
  );
  const gridPreferences = useSelector((state: RootState) =>
    state.userPreferenceData?.userGridPreferences?.find(item => item.id === CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG),
  );
  const userPreference = useSelector(selectUserPreference);
  const userPreferenceLoaded = useSelector(selectUserPreferenceLoaded);
  const columnVisibilityModel = useSelector(selectColumnVisibilityModel);
  const gridApiRef = useGridApiRef();
  const filtersHydratedRef = React.useRef(false);
  const skipNextDurablePersistRef = React.useRef(false);

  const [activeTab, setActiveTab] = React.useState<CandidateEngagementTabKey>('inquiry');
  const [selectedView, setSelectedView] = React.useState(t('global.filterPreferences.selectFilters'));
  const [isSaveAsModalOpen, setSaveAsModalOpen] = React.useState(false);
  const [isSaveModalOpen, setSaveModalOpen] = React.useState(false);
  const [isAlertModalOpen, setAlertModalOpen] = React.useState(false);
  const [existingFilterSet, setExistingFilterSet] = React.useState('');
  const [saveAsName, setSaveAsName] = React.useState('');
  const [disableSaveButton, setDisableSaveButton] = React.useState(true);
  const [saveAsErrorText, setSaveAsErrorText] = React.useState('');
  const [candidateFilterDraft, setCandidateFilterDraft] = React.useState<ICandidateAggregateSearch>(
    initialCandidateAggregateSearch,
  );
  const [candidateFilterApplied, setCandidateFilterApplied] = React.useState<ICandidateAggregateSearch>(
    initialCandidateAggregateSearch,
  );
  const [brandFilterDraft, setBrandFilterDraft] = React.useState<ISelection[]>([]);
  const [brandFilterApplied, setBrandFilterApplied] = React.useState<ISelection[]>([]);

  const [createdDateFilter, setCreatedDateFilter] = React.useState<{
    startDate?: Date;
    endDate?: Date;
  } | null>(null);
  const [leadOriginInput, setLeadOriginInput] = React.useState('');
  /** Committed filter sent to the API / chips; updated on field blur (click-away). */
  const [leadOriginApplied, setLeadOriginApplied] = React.useState('');

  const commitLeadOriginFilter = React.useCallback(() => {
    setLeadOriginApplied(leadOriginInput.trim());
  }, [leadOriginInput]);
  const [brandOptions, setBrandOptions] = React.useState<IBrandOption[]>([]);
  const [apiRows, setApiRows] = React.useState<IInquiryRecord[]>([]);
  const [apiSummary, setApiSummary] = React.useState({
    candidates: 0,
    openInquiries: 0,
    totalEngagementsActivity: 0,
    activeReferrals: 0,
    applications: 0,
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);
  const [sortColumn, setSortColumn] = React.useState('createdDate');
  const [sortDirection, setSortDirection] = React.useState<'ASC' | 'DESC'>('DESC');

  const allRows = apiRows;

  React.useEffect(() => {
    let disposed = false;
    setIsLoading(true);

    const candidateIds = (candidateFilterApplied?.selections || [])
      .map(s => (Number(s.candidateId || s.value || 0)))
      .filter(Boolean);

    const trimmedFirst = (candidateFilterApplied?.firstName ?? '').trim();
    const trimmedLast = (candidateFilterApplied?.lastName ?? '').trim();
    const candidateNameFromAggregate =
      candidateIds.length > 0
        ? undefined
        : [trimmedFirst, trimmedLast].filter(Boolean).join(' ') || undefined;

    const brandIds = brandFilterApplied
      .map(b => Number(b.id || 0))
      .filter(Boolean);

    const recordTypes = activeTab !== 'all' ? [activeTab] : undefined;

    const params = {
      candidateIds: candidateIds.length > 0 ? candidateIds : undefined,
      candidateName: candidateNameFromAggregate,
      brandIds: brandIds.length > 0 ? brandIds : undefined,
      recordTypes,
      createdDateMin: createdDateFilter?.startDate?.toISOString(),
      createdDateMax: createdDateFilter?.endDate?.toISOString(),
      leadOrigin: leadOriginApplied || undefined,
      sortColumn,
      sortDirection,
      pageNumber: 1,
      pageSize: 200,
    };

    candidateService
      .getEngagementInquiries(params)
      .then(response => {
        if (disposed) return;
        if (httpSuccess(response?.status)) {
          const data = response.data as {
            summary: typeof apiSummary;
            items: Array<{
              leadInquiryId: string;
              candidateId: string;
              candidate: { firstName: string; lastName: string };
              recordType: string;
              brandId?: string;
              brand: string;
              discipline: string;
              leadOrigin: string;
              leadSourceCategory: string;
              ownerName: string;
              externalInquiryId?: string;
              jobId?: number | null;
              orderId?: number | null;
              createdDate: string;
              lastUpdatedDate: string;
            }>;
          };
          const records = (data?.items ?? []).map(r => ({
            id: String(r.leadInquiryId),
            leadInquiryId: String(r.leadInquiryId ?? ''),
            candidateId: Number(r.candidateId),
            candidateName: `${r.candidate?.firstName ?? ''} ${r.candidate?.lastName ?? ''}`.trim(),
            recordType: (r.recordType ?? 'inquiry') as IInquiryRecord['recordType'],
            brandId: r.brandId != null && r.brandId !== '' ? String(r.brandId) : '',
            brand: r.brand ?? '',
            discipline: r.discipline ?? '',
            leadOrigin: r.leadOrigin ?? '',
            leadSourceCategory: r.leadSourceCategory ?? '',
            ownerName: r.ownerName ?? '',
            externalInquiryId: r.externalInquiryId ?? '',
            jobId: r.jobId ?? null,
            orderId: r.orderId ?? null,
            createdDate: formatIsoToUsDate(r.createdDate),
            lastUpdatedDate: formatIsoToUsDate(r.lastUpdatedDate),
          }));
          setApiRows(records);
          setApiSummary({
            candidates: data?.summary?.candidates ?? 0,
            openInquiries: data?.summary?.openInquiries ?? 0,
            totalEngagementsActivity: data?.summary?.totalEngagementsActivity ?? 0,
            activeReferrals: data?.summary?.activeReferrals ?? 0,
            applications: data?.summary?.applications ?? 0,
          });
        } else {
          setApiRows([]);
          setApiSummary({
            candidates: 0,
            openInquiries: 0,
            totalEngagementsActivity: 0,
            activeReferrals: 0,
            applications: 0,
          });
        }
      })
      .catch(() => {
        if (disposed) return;
        setApiRows([]);
        setApiSummary({
          candidates: 0,
          openInquiries: 0,
          totalEngagementsActivity: 0,
          activeReferrals: 0,
          applications: 0,
        });
      })
      .finally(() => {
        if (!disposed) {
          setIsLoading(false);
        }
      });

    return () => {
      disposed = true;
    };
  }, [
    activeTab,
    candidateFilterApplied,
    brandFilterApplied,
    createdDateFilter,
    leadOriginApplied,
    sortColumn,
    sortDirection,
    reloadToken,
    candidateService,
  ]);

  const baseColumns = React.useMemo<GridColDef<IInquiryRecord>[]>(
    () => [
      {
        field: 'leadInquiryId',
        headerName: t('candidateEngagement.columns.leadInquiryId'),
        width: 130,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'brandId',
        headerName: t('candidateEngagement.columns.brandId'),
        width: 100,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'candidateName',
        headerName: t('candidateEngagement.columns.candidateName'),
        width: 160,
        valueFormatter: p => formatEngagementGridCell(p.value),
        renderCell: params => {
          const row = params.row;
          const display = formatEngagementGridCell(params.value);
          const candidateId = row.candidateId;
          const brandPart = row.brandId != null ? String(row.brandId).trim() : '';
          const canLink =
            typeof candidateId === 'number' &&
            !Number.isNaN(candidateId) &&
            candidateId > 0 &&
            brandPart.length > 0;

          if (!canLink) {
            return (
              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                {display}
              </Box>
            );
          }

          return (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
              <RouterLink
                to={`/candidate/${candidateId}/${brandPart}`}
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  fontSize: theme.typography.body1.fontSize,
                }}
                onClick={e => {
                  e.stopPropagation();
                  dispatch(navigationProfilesAction.setNavigationFlag({ navigationFromGrid: true }));
                  dispatch(navigationProfilesAction.setCurrentRowId({ rowId: row.id }));
                }}
              >
                {display}
              </RouterLink>
            </Box>
          );
        },
      },
      {
        field: 'candidateId',
        headerName: t('candidateEngagement.columns.candidateId'),
        width: 120,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'brand',
        headerName: t('candidateEngagement.columns.brand'),
        width: 140,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'discipline',
        headerName: t('candidateEngagement.columns.discipline'),
        width: 160,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'leadOrigin',
        headerName: t('candidateEngagement.columns.leadOrigin'),
        width: 150,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'leadSourceCategory',
        headerName: t('candidateEngagement.columns.leadSourceCategory'),
        width: 150,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'ownerName',
        headerName: t('candidateEngagement.columns.owner'),
        width: 130,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'externalInquiryId',
        headerName: t('candidateEngagement.columns.externalInquiryId'),
        width: 140,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'jobId',
        headerName: t('candidateEngagement.columns.jobId'),
        width: 100,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'orderId',
        headerName: t('candidateEngagement.columns.orderId'),
        width: 100,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'createdDate',
        headerName: t('candidateEngagement.columns.inquiryCreated'),
        width: 130,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
      {
        field: 'lastUpdatedDate',
        headerName: t('candidateEngagement.columns.lastUpdated'),
        minWidth: 130,
        flex: 1,
        valueFormatter: p => formatEngagementGridCell(p.value),
      },
    ],
    [dispatch, t, theme],
  );

  const defaultColumns = React.useMemo<GridColDef<IInquiryRecord>[]>(
    () => [
      {
        field: 'recordType',
        headerName: t('candidateEngagement.columns.recordType'),
        width: 120,
        valueFormatter: params => {
          const raw = String(params.value ?? '').trim();
          if (!raw) {
            return formatEngagementGridCell('');
          }
          return `${raw.charAt(0).toUpperCase()}${raw.slice(1)}`;
        },
      },
      ...baseColumns,
    ],
    [baseColumns, t],
  );

  const defaultColumnVisibilityModel = React.useMemo((): GridColumnVisibilityModel => {
    const model: GridColumnVisibilityModel = {};
    defaultColumns.forEach(col => {
      if (col.field) {
        model[col.field] = true;
      }
    });
    return model;
  }, [defaultColumns]);

  const [displayColumns, setDisplayColumns] = React.useState<GridColDef<IInquiryRecord>[]>([]);
  const [gridInitialState, setGridInitialState] = React.useState<
    { columns: { columnVisibilityModel: GridColumnVisibilityModel } } | undefined
  >();

  const getColumnPreferences = React.useCallback(
    (cols: GridColDef<IInquiryRecord>[]) => {
      try {
        const columnPreferences = gridPreferences?.value;
        const filterSetColumns = gridPreferences?.value?.filterAttributes?.filterSet?.find(
          item => item.name === gridPreferences?.value?.selectedFilter?.name,
        )?.columns;
        if (columnPreferences && (columnPreferences.columns || filterSetColumns)) {
          const newColumns = cols.map(column => {
            const foundIndex = (filterSetColumns ?? columnPreferences.columns).findIndex(
              item => item?.field === column?.field,
            );
            if (foundIndex !== -1) {
              return {
                ...column,
                ...(filterSetColumns ?? columnPreferences.columns)[foundIndex],
              };
            }
            return column;
          });
          return newColumns.sort((a, b) => {
            const filterSetColumnsIndexA = (filterSetColumns ?? columnPreferences.columns).findIndex(
              i => i.field === a.field,
            );
            const filterSetColumnsIndexB = (filterSetColumns ?? columnPreferences.columns).findIndex(
              i => i.field === b.field,
            );

            const indexA = filterSetColumnsIndexA === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexA;
            const indexB = filterSetColumnsIndexB === -1 ? Number.MAX_SAFE_INTEGER : filterSetColumnsIndexB;

            return indexA - indexB;
          });
        }
        return cols;
      } catch (err) {
        trackException({
          exception: err,
          properties: { columns: cols, grid: CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG, surface: 'CandidateEngagement' },
        });
        return cols;
      }
    },
    [gridPreferences],
  );

  const determineGridColumns = React.useCallback(
    (isDefault = false) => {
      const next = isDefault ? _.cloneDeep(defaultColumns) : getColumnPreferences(_.cloneDeep(defaultColumns));
      setDisplayColumns(next);
    },
    [defaultColumns, getColumnPreferences],
  );

  const savePreferences = React.useCallback(
    ({
      columns: gridColumns,
      visibilityModel,
    }: {
      columns: XGridColumns;
      visibilityModel: GridColumnVisibilityModel;
    }) => {
      if (!displayColumns || displayColumns.length === 0 || !userPreference.loaded || defaultColumns.length === 0) {
        return;
      }
      const currentGridColumns = new GridColumns(gridColumns).columns;

      const i = (gridPreferences?.value?.filterAttributes?.filterSet || [])?.findIndex(
        item => item.name === gridPreferences?.value?.selectedFilter?.name,
      );

      if (currentGridColumns && currentGridColumns.length > 0 && currentGridColumns[0].field === '__check__') {
        currentGridColumns.shift();
      }

      const comparableColumns = new GridColumns(
        i !== -1 ? gridPreferences?.value?.filterAttributes?.filterSet[i]?.columns : gridPreferences?.value?.columns,
        i !== -1
          ? gridPreferences?.value?.filterAttributes?.filterSet[i]?.columnVisibilityModel
          : gridPreferences?.value?.columnVisibilityModel,
      );
      const columnsEqual = comparableColumns.isEqual(currentGridColumns);
      const visibilityEqual = comparableColumns.isVisibilityModelEqual(visibilityModel);

      if (!columnsEqual || !visibilityEqual) {
        type FilterSetRow = {
          name?: string;
          columns?: XGridColumns;
          columnVisibilityModel?: GridColumnVisibilityModel;
        };
        let newFilterSet: FilterSetRow[] | undefined;
        if (i !== -1) {
          newFilterSet = _.cloneDeep(gridPreferences?.value?.filterAttributes?.filterSet) as
            | FilterSetRow[]
            | undefined;
          if (newFilterSet?.[i]) {
            newFilterSet[i] = {
              ...newFilterSet[i],
              columns: currentGridColumns,
              columnVisibilityModel: visibilityModel,
            };
          }
        }

        const preferenceData = {
          id: CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG,
          value: {
            ...(gridPreferences?.value || {}),
            filterAttributes: {
              ...gridPreferences?.value?.filterAttributes,
              filterSet: newFilterSet ?? gridPreferences?.value?.filterAttributes?.filterSet,
            },
            columns: currentGridColumns,
            columnVisibilityModel: visibilityModel,
          },
        };

        dispatch(userDevicePreferenceActions.saveUserGridPreferenceAction(preferenceData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayColumns, userPreference.loaded, defaultColumns.length, gridPreferences?.value, dispatch],
  );

  React.useEffect(() => {
    if (userPreference?.loaded) {
      dispatch(gridStateActions.setGrid(CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG));
    }
  }, [userPreference?.loaded, dispatch]);

  React.useEffect(() => {
    if (!userPreference?.loaded || defaultColumns.length === 0) {
      return;
    }
    setDisplayColumns(getColumnPreferences(_.cloneDeep(defaultColumns)));
  }, [
    userPreference?.loaded,
    defaultColumns,
    getColumnPreferences,
    gridPreferences?.value?.columns,
    gridPreferences?.value?.filterAttributes?.filterSet,
    gridPreferences?.value?.selectedFilter?.name,
  ]);

  const displayColumnsSignature = React.useMemo(() => JSON.stringify(displayColumns), [displayColumns]);

  React.useEffect(() => {
    if (!userPreference?.loaded || displayColumns.length === 0) {
      return;
    }
    const savedModel = gridPreferences?.value?.columnVisibilityModel;
    const resolvedVisibilityModel = savedModel || defaultColumnVisibilityModel;
    if (
      savedModel &&
      !Object.keys(defaultColumnVisibilityModel).every(key => Object.keys(savedModel).includes(key))
    ) {
      const cols = { ...defaultColumnVisibilityModel };
      _.merge(cols, savedModel);
      dispatch(gridStateActions.setColumnVisibilityModel(cols));
      determineGridColumns(false);
      setGridInitialState({ columns: { columnVisibilityModel: cols } });
    } else {
      dispatch(gridStateActions.setColumnVisibilityModel(resolvedVisibilityModel));
      setGridInitialState({ columns: { columnVisibilityModel: resolvedVisibilityModel } });
    }
  }, [
    displayColumns.length,
    displayColumnsSignature,
    gridPreferences?.value?.columnVisibilityModel,
    userPreference?.loaded,
    defaultColumnVisibilityModel,
    determineGridColumns,
    dispatch,
  ]);

  React.useEffect(() => {
    let disposed = false;

    getSearchFilterOptions('brands', true).then(response => {
      if (disposed) {
        return;
      }

      const options = getMultiSelectOptions(response || [], { withoutAllOption: true }) as IBrandOption[];
      setBrandOptions(options);
    });

    return () => {
      disposed = true;
    };
  }, []);

  const brandDropdownOptions = React.useMemo<ISelection[]>(() => {
    if (brandOptions.length === 0) {
       // Do not generate fallback options with synthetic IDs; wait for API to load real brand options
       return [];
    }

    return brandOptions
      .map(option => {
        const value = option.value || option.name || '';
        const label = option.name || option.value || '';
        return {
          id: option.id,
          name: label,
          value,
        };
      })
      .filter(item => item.value && item.name);
    }, [brandOptions]);

  const filteredRows = allRows;

  const tabTitle = React.useMemo(() => {
    const map: Record<CandidateEngagementTabKey, string> = {
      inquiry: t('candidateEngagement.tabs.inquiries'),
      engagement: t('candidateEngagement.tabs.engagementActivity'),
      referral: t('candidateEngagement.tabs.referrals'),
      application: t('candidateEngagement.tabs.applications'),
      all: t('candidateEngagement.tabs.unifiedOverview'),
    };

    return map[activeTab];
  }, [activeTab, t]);

  const tabs = React.useMemo(
    () => [
      { label: t('candidateEngagement.tabs.inquiries'), value: 'inquiry' as const },
      { label: t('candidateEngagement.tabs.engagementActivity'), value: 'engagement' as const },
      { label: t('candidateEngagement.tabs.referrals'), value: 'referral' as const },
      { label: t('candidateEngagement.tabs.applications'), value: 'application' as const },
      { label: t('candidateEngagement.tabs.unifiedOverview'), value: 'all' as const },
    ],
    [t],
  );

  const candidateChips = React.useMemo(() => {
    const chips = [] as { label: string; value: unknown; enableDelete: boolean }[];

    const candidateFilterLabel = t('candidateEngagement.filters.candidate');

    (candidateFilterApplied?.selections || []).forEach(selection => {
      const candidateId = selection.candidateId || selection.value || selection.id;
      const candidateName = selection.name || '';
      const valuePart = candidateName ? `${candidateName} (${candidateId})` : `(${candidateId})`;

      chips.push({
        label: `${candidateFilterLabel}: ${valuePart}`,
        enableDelete: true,
        value: { key: 'selections', value: selection },
      });
    });

    const restLabels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      goesBy: 'Goes By',
      cid: 'CID',
      otherName: 'Other Name',
      phoneNumber: 'Phone',
      email: 'Email',
    };

    Object.entries(restLabels).forEach(([key, label]) => {
      const value = candidateFilterApplied?.[key];

      if (value) {
        chips.push({
          label: `${label}: ${value}`,
          enableDelete: true,
          value: { key, value },
        });
      }
    });

    return chips;
  }, [candidateFilterApplied, t]);

  const brandChips = React.useMemo(
    () => ({
      brand: brandFilterApplied.map(item => ({
        label: `${t('candidateEngagement.filters.brand')}: ${item.name || String(item.value || '')}`,
        value: item.value,
        enableDelete: true,
      })),
    }),
    [brandFilterApplied, t],
  );

  const leadOriginChips = React.useMemo(
    () =>
      leadOriginApplied
        ? [
            {
              label: `${t('candidateEngagement.filters.leadOrigin')}: ${leadOriginApplied}`,
              value: leadOriginApplied,
              enableDelete: true,
            },
          ]
        : [],
    [leadOriginApplied, t],
  );

  const allChips = React.useMemo(
    () => ({
      candidate: candidateChips,
      brand: brandChips.brand,
      leadOrigin: leadOriginChips,
    }),
    [brandChips.brand, candidateChips, leadOriginChips],
  );

  const removeCandidateChip = React.useCallback(
    (chip: { value?: { key?: string; value?: unknown } }) => {
      const chipKey = chip?.value?.key;

      if (!chipKey) {
        return;
      }

      if (chipKey === 'selections') {
        const selection = chip?.value?.value as ISelection | undefined;
        const removeValue = String(selection?.value || '').toLowerCase();
        setCandidateFilterApplied(prev => ({
          ...prev,
          selections: (prev.selections || []).filter(
            sel => String(sel.value || '').toLowerCase() !== removeValue,
          ),
        }));
        setCandidateFilterDraft(prev => ({
          ...prev,
          selections: (prev.selections || []).filter(
            sel => String(sel.value || '').toLowerCase() !== removeValue,
          ),
        }));
        return;
      }

      setCandidateFilterApplied(prev => ({ ...prev, [chipKey]: null }));
      setCandidateFilterDraft(prev => ({ ...prev, [chipKey]: null }));
    },
    [setCandidateFilterApplied, setCandidateFilterDraft],
  );

  const removeBrandChip = React.useCallback(
    (chipValue: unknown) => {
      const removeValue = String(chipValue || '').toLowerCase();
      setBrandFilterApplied(prev => prev.filter(item => String(item.value || '').toLowerCase() !== removeValue));
      setBrandFilterDraft(prev => prev.filter(item => String(item.value || '').toLowerCase() !== removeValue));
    },
    [setBrandFilterApplied, setBrandFilterDraft],
  );

  const clearAllFilters = React.useCallback(() => {
    setCandidateFilterDraft(initialCandidateAggregateSearch);
    setCandidateFilterApplied(initialCandidateAggregateSearch);
    setBrandFilterDraft([]);
    setBrandFilterApplied([]);
    setCreatedDateFilter(null);
    setLeadOriginInput('');
    setLeadOriginApplied('');
  }, []);

  const applyFilterEntries = React.useCallback((entries: [string, unknown][]) => {
    const applied = entriesToAppliedState(entries);
    setCandidateFilterApplied(applied.candidateFilter);
    setCandidateFilterDraft(applied.candidateFilter);
    setBrandFilterApplied(applied.brandFilter);
    setBrandFilterDraft(applied.brandFilter);
    setCreatedDateFilter(applied.createdDateFilter);
    setLeadOriginInput(applied.leadOrigin);
    setLeadOriginApplied(applied.leadOrigin);
    setActiveTab(applied.activeTab);
  }, []);

  const engagementHydrationKey = JSON.stringify(filterPreferences?.value?.selectedFilter ?? null);

  React.useEffect(() => {
    if (!userPreferenceLoaded) {
      return;
    }

    filtersHydratedRef.current = false;
    skipNextDurablePersistRef.current = true;

    const restoreSelectedLabelFromPreference = () => {
      const selectedFromPreference = filterPreferences?.value?.selectedFilter;
      if (selectedFromPreference?.name) {
        setSelectedView(selectedFromPreference.name);
      } else {
        setSelectedView(t('global.filterPreferences.selectFilters'));
      }
    };

    const sessionEntries = getSessionValue(
      SessionKey.candidateEngagementInquiriesSessionFilters,
      StorageType.sessionStorage,
    ) as [string, unknown][] | undefined;
    if (Array.isArray(sessionEntries) && sessionEntries.length > 0) {
      applyFilterEntries(sessionEntries);
      restoreSelectedLabelFromPreference();
      filtersHydratedRef.current = true;
      return;
    }

    const rawDurable = filterPreferences?.value?.[CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY];
    const durableEntries =
      Array.isArray(rawDurable) && rawDurable.length > 0 ? (rawDurable as [string, unknown][]) : null;
    if (durableEntries) {
      applyFilterEntries(durableEntries);
      restoreSelectedLabelFromPreference();
      filtersHydratedRef.current = true;
      return;
    }

    const selected = filterPreferences?.value?.selectedFilter;
    if (selected?.name && Array.isArray(selected.value)) {
      applyFilterEntries(selected.value as [string, unknown][]);
      setSelectedView(selected.name);
    } else {
      setSelectedView(t('global.filterPreferences.selectFilters'));
    }
    filtersHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- engagementHydrationKey serializes selectedFilter
  }, [userPreferenceLoaded, engagementHydrationKey, applyFilterEntries, t]);

  React.useEffect(() => {
    if (!userPreferenceLoaded || !filterPreferences?.value) {
      return;
    }
    const noSelectionLabel = t('global.filterPreferences.selectFilters');
    const sets = filterPreferences.value.filterAttributes?.filterSet || [];
    const names = sets.map(s => s.name);
    if (selectedView !== noSelectionLabel && !names.includes(selectedView)) {
      setSelectedView(noSelectionLabel);
      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          id: CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
          value: {
            ...filterPreferences.value,
            selectedFilter: null,
          },
        }),
      );
    }
  }, [
    userPreferenceLoaded,
    filterPreferences?.value?.filterAttributes?.filterSet,
    selectedView,
    t,
    dispatch,
    filterPreferences?.value,
  ]);

  React.useEffect(() => {
    if (!userPreferenceLoaded || !filtersHydratedRef.current) {
      return;
    }
    const snap = buildSnapshotFromApplied({
      candidateFilterApplied,
      brandFilterApplied,
      createdDateFilter,
      leadOriginApplied,
      activeTab,
    });
    setSessionValue(
      SessionKey.candidateEngagementInquiriesSessionFilters,
      snapshotToEntries(snap),
      StorageType.sessionStorage,
    );
  }, [
    userPreferenceLoaded,
    candidateFilterApplied,
    brandFilterApplied,
    createdDateFilter,
    leadOriginApplied,
    activeTab,
  ]);

  React.useEffect(() => {
    if (!userPreferenceLoaded || !filtersHydratedRef.current) {
      return;
    }
    if (skipNextDurablePersistRef.current) {
      skipNextDurablePersistRef.current = false;
      return;
    }

    const entries = snapshotToEntries(
      buildSnapshotFromApplied({
        candidateFilterApplied,
        brandFilterApplied,
        createdDateFilter,
        leadOriginApplied,
        activeTab,
      }),
    );

    const prevEntriesRaw = filterPreferences?.value?.[CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY];
    const prevEntries =
      Array.isArray(prevEntriesRaw) && prevEntriesRaw.length > 0
        ? (prevEntriesRaw as [string, unknown][])
        : [];
    if (normalizedSnapshotJson(entries) === normalizedSnapshotJson(prevEntries)) {
      return;
    }

    dispatch(
      userDevicePreferenceActions.saveUserGridPreferenceAction({
        id: CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
        value: {
          ...(filterPreferences?.value ?? { filterAttributes: { filterSet: [] } }),
          [CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY]: entries,
        },
      }),
    );
  }, [
    userPreferenceLoaded,
    candidateFilterApplied,
    brandFilterApplied,
    createdDateFilter,
    leadOriginApplied,
    activeTab,
    dispatch,
    filterPreferences?.value,
  ]);

  const namedFilterSets = React.useMemo(
    () =>
      _.cloneDeep(filterPreferences?.value?.filterAttributes?.filterSet || []).sort((a, b) =>
        (a?.name ?? '').toLowerCase().localeCompare((b?.name ?? '').toLowerCase()),
      ),
    [filterPreferences?.value?.filterAttributes?.filterSet],
  );

  const filterOptionNames = React.useMemo(() => namedFilterSets.map(f => f.name), [namedFilterSets]);

  const selectFiltersLabel = t('global.filterPreferences.selectFilters');

  const currentEntries = React.useMemo(
    () =>
      snapshotToEntries(
        buildSnapshotFromApplied({
          candidateFilterApplied,
          brandFilterApplied,
          createdDateFilter,
          leadOriginApplied,
          activeTab,
        }),
      ),
    [candidateFilterApplied, brandFilterApplied, createdDateFilter, leadOriginApplied, activeTab],
  );

  const currentSnapshot = React.useMemo(
    () =>
      buildSnapshotFromApplied({
        candidateFilterApplied,
        brandFilterApplied,
        createdDateFilter,
        leadOriginApplied,
        activeTab,
      }),
    [candidateFilterApplied, brandFilterApplied, createdDateFilter, leadOriginApplied, activeTab],
  );

  const filtersApplied = React.useMemo(() => checkEngagementFiltersApplied(currentSnapshot), [currentSnapshot]);

  const selectedSetEntry = React.useMemo(
    () => namedFilterSets.find(item => item.name === selectedView),
    [namedFilterSets, selectedView],
  );

  const isFilterChanged = React.useMemo(() => {
    if (selectedView === selectFiltersLabel || !selectedSetEntry) {
      return false;
    }
    const saved = Array.isArray(selectedSetEntry.value) ? (selectedSetEntry.value as [string, unknown][]) : [];
    return normalizedSnapshotJson(currentEntries) !== normalizedSnapshotJson(saved);
  }, [selectedView, selectFiltersLabel, selectedSetEntry, currentEntries]);

  const { enableSaveButton, enableSaveAsButton } = React.useMemo(() => {
    let enableSave = false;
    let enableSaveAs = false;
    if (selectedView !== selectFiltersLabel && selectedSetEntry) {
      if (isFilterChanged) {
        enableSaveAs = true;
        enableSave = true;
      }
    } else if (selectedView === selectFiltersLabel && filtersApplied) {
      enableSaveAs = true;
    }
    return { enableSaveButton: enableSave, enableSaveAsButton: enableSaveAs };
  }, [selectedView, selectFiltersLabel, selectedSetEntry, isFilterChanged, filtersApplied]);

  const isDuplicateFilterFingerprint = React.useCallback(() => {
    let isDuplicate = false;
    (filterPreferences?.value?.filterAttributes?.filterSet || []).every(item => {
      if (
        _.isEqual(
          normalizedSnapshotJson(Array.isArray(item.value) ? (item.value as [string, unknown][]) : []),
          normalizedSnapshotJson(currentEntries),
        )
      ) {
        isDuplicate = true;
        setExistingFilterSet(item.name);
        return false;
      }
      return true;
    });
    return isDuplicate;
  }, [filterPreferences?.value?.filterAttributes?.filterSet, currentEntries]);

  const handleFilterOptionsChange = (value: string) => {
    if (!value || (value === selectedView && !isFilterChanged)) {
      return;
    }
    const filterSet = filterPreferences?.value?.filterAttributes?.filterSet?.find(item => item.name === value);
    if (!filterSet || !Array.isArray(filterSet.value)) {
      return;
    }
    removeSessionValue(SessionKey.candidateEngagementInquiriesReset, StorageType.sessionStorage);
    removeSessionValue(SessionKey.candidateEngagementInquiriesSessionFilters, StorageType.sessionStorage);
    const selectedFilter = {
      name: filterSet.name,
      value: filterSet.value,
      isDefault: filterSet.isDefault,
    };
    dispatch(
      userDevicePreferenceActions.saveUserGridPreferenceAction({
        id: CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
        value: {
          ...(filterPreferences?.value ?? { filterAttributes: { filterSet: [] } }),
          selectedFilter,
          [CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY]: filterSet.value as [string, unknown][],
        },
      }),
    );
    applyFilterEntries(filterSet.value as [string, unknown][]);
    setSelectedView(value);
  };

  const handleFilterNameChange = (val: string) => {
    const trimmed = val?.trim() ?? '';
    const isNamePresent = (filterPreferences?.value?.filterAttributes?.filterSet || []).some(
      filter => filter?.name?.trim()?.toLowerCase() === trimmed.toLowerCase(),
    );
    setDisableSaveButton(trimmed.length <= 1 || trimmed.length >= 31 || isNamePresent);
    setSaveAsName(val);
    setSaveAsErrorText(isNamePresent ? t('global.filterPreferences.nameAlreadyExists') : '');
  };

  const addNewFilter = () => {
    const trimmedName = saveAsName.trim();
    const lowered = trimmedName.toLowerCase();
    if (!trimmedName) {
      return;
    }
    removeSessionValue(SessionKey.candidateEngagementInquiriesReset, StorageType.sessionStorage);
    removeSessionValue(SessionKey.candidateEngagementInquiriesSessionFilters, StorageType.sessionStorage);
    if (
      (filterPreferences?.value?.filterAttributes?.filterSet || []).filter(
        item => item.name.toLowerCase() === lowered,
      ).length > 0
    ) {
      setSaveAsErrorText(t('global.filterPreferences.nameAlreadyExists'));
      return;
    }

    if (isDuplicateFilterFingerprint()) {
      setSaveAsModalOpen(false);
      setAlertModalOpen(true);
      return;
    }

    const entries = snapshotToEntries(
      buildSnapshotFromApplied({
        candidateFilterApplied,
        brandFilterApplied,
        createdDateFilter,
        leadOriginApplied,
        activeTab,
      }),
    );
    const newFilterOption = {
      name: trimmedName,
      value: entries,
    };
    const prevValue = filterPreferences?.value || {
      filterAttributes: { filterSet: [] as { name: string; value: [string, unknown][] }[] },
      selectedFilter: null as { name: string; value: [string, unknown][]; isDefault?: boolean } | null,
    };
    const nextValue = {
      ...prevValue,
      filterAttributes: {
        ...prevValue.filterAttributes,
        filterSet: [...(prevValue.filterAttributes?.filterSet ?? []), newFilterOption],
      },
      selectedFilter: {
        name: newFilterOption.name,
        value: newFilterOption.value,
        isDefault: false,
      },
      [CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY]: entries,
    };
    dispatch(
      userDevicePreferenceActions.saveUserGridPreferenceAction({
        id: CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
        value: nextValue,
      }),
    );
    dispatch(
      globalActions.setSnackBar({
        message: t('search.filter.addFilterSuccess'),
        severity: 'success',
      }),
    );
    setSaveAsModalOpen(false);
    setDisableSaveButton(true);
    setSaveAsName('');
    setSaveAsErrorText('');
    setSelectedView(trimmedName);
  };

  const handleSaveExistingFilter = () => {
    setSaveModalOpen(
      !(filterPreferences?.value?.filterAttributes?.filterSet || []).some(item => {
        if (item.name === selectedView) {
          return false;
        }
        const saved = Array.isArray(item.value) ? (item.value as [string, unknown][]) : [];
        const duplicateExist = normalizedSnapshotJson(saved) === normalizedSnapshotJson(currentEntries);
        if (duplicateExist) {
          setAlertModalOpen(true);
          setExistingFilterSet(item.name);
          setSaveAsModalOpen(false);
        }
        return duplicateExist;
      }),
    );
  };

  const updateExistingFilter = () => {
    const filterPrefClone = _.cloneDeep(filterPreferences);
    if (!filterPrefClone?.value?.selectedFilter?.name) {
      setSaveModalOpen(false);
      return;
    }
    const entries = snapshotToEntries(
      buildSnapshotFromApplied({
        candidateFilterApplied,
        brandFilterApplied,
        createdDateFilter,
        leadOriginApplied,
        activeTab,
      }),
    );
    removeSessionValue(SessionKey.candidateEngagementInquiriesReset, StorageType.sessionStorage);
    removeSessionValue(SessionKey.candidateEngagementInquiriesSessionFilters, StorageType.sessionStorage);
    dispatch(
      userDevicePreferenceActions.saveUserGridPreferenceAction({
        id: CANDIDATE_ENGAGEMENT_INQUIRIES_FILTER_VIEWS_PREFERENCE_ID,
        value: {
          ...(filterPrefClone?.value ?? {}),
          filterAttributes: {
            ...(filterPrefClone?.value?.filterAttributes ?? {}),
            filterSet: (_.cloneDeep(filterPrefClone?.value?.filterAttributes?.filterSet) ?? []).map(item => {
              if (item.name === filterPrefClone?.value?.selectedFilter?.name) {
                return { ...item, value: entries };
              }
              return item;
            }),
          },
          selectedFilter: {
            ...filterPrefClone.value.selectedFilter,
            value: entries,
          },
          [CURRENT_LEADS_FILTER_SNAPSHOT_PREF_KEY]: entries,
        },
      }),
    );
    dispatch(
      globalActions.setSnackBar({
        message: t('search.filter.addFilterSuccess'),
        severity: 'success',
      }),
    );
    setSaveModalOpen(false);
  };

  React.useEffect(() => {
    dispatch(navigationStickActions.setSelectedMenuByKey('candidateEngagement'));
    dispatch(navigationStickActions.setSelectedSubMenu(undefined));
  }, [dispatch]);

  return (
    <DetailsPage
      title={t('candidateEngagement.title')}
      titleActions={
        <div className={classes.viewActionsRow}>
          <Typography variant="body1" className={classes.viewActionsLabel}>
            {t('candidateEngagement.myViews')}
          </Typography>
          <FilterPreferenceSelector
            filterOptions={filterOptionNames}
            selectedFilterOption={selectedView}
            setMangeFilters={() => undefined}
            handleFilterOptionsChange={handleFilterOptionsChange}
            size="medium"
            showManageFilters={false}
          />
          <Button
            disableElevation
            color="primary"
            size="medium"
            className={classes.viewButton}
            variant="contained"
            onClick={() => handleSaveExistingFilter()}
            disabled={
              filterOptionNames.length === 0 ||
              selectedView === selectFiltersLabel ||
              !enableSaveButton
            }
          >
            {t('candidateEngagement.save')}
          </Button>
          <Button
            disableElevation
            color="primary"
            size="medium"
            className={classes.viewButton}
            variant="contained"
            onClick={() => {
              setSaveAsModalOpen(true);
              setSaveAsName('');
              setSaveAsErrorText('');
              setDisableSaveButton(true);
            }}
            disabled={!(enableSaveAsButton && filtersApplied)}
          >
            {t('candidateEngagement.saveAs')}
          </Button>
        </div>
      }
      fullPage
      fullHeight
      head={
        <div className={classes.root}>
          <div className={classes.statsContainer}>
            <Card className={classes.statCard}>
              <CardContent className={classes.statCardCandidatesContent}>
                <PeopleOutlineIcon className={classes.statCandidatesIcon} />
                <Typography className={classes.statLabel}>{t('candidateEngagement.metrics.candidates')}</Typography>
                <Typography className={classes.statValue}>
                  {Number(apiSummary.candidates).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
            <Card className={classes.statCard}>
              <CardContent className={classes.statCardIconContent}>
                <div className={classes.statIconBox} style={{ backgroundColor: '#0C75BC' }}>
                  <ListAltIcon style={{ color: '#fff', fontSize: 26 }} />
                </div>
                <div>
                  <Typography className={classes.statValue}>
                    {Number(apiSummary.openInquiries).toLocaleString()}
                  </Typography>
                  <Typography className={classes.statLabel}>{t('candidateEngagement.metrics.openInquiries')}</Typography>
                </div>
              </CardContent>
            </Card>
            <Card className={classes.statCard}>
              <CardContent className={classes.statCardIconContent}>
                <div className={classes.statIconBox} style={{ backgroundColor: '#00968F' }}>
                  <ShowChartIcon style={{ color: '#fff', fontSize: 26 }} />
                </div>
                <div>
                  <Typography className={classes.statValue}>
                    {Number(apiSummary.totalEngagementsActivity).toLocaleString()}
                  </Typography>
                  <Typography className={classes.statLabel}>{t('candidateEngagement.metrics.totalEngagementsActivity')}</Typography>
                </div>
              </CardContent>
            </Card>
            <Card className={classes.statCard}>
              <CardContent className={classes.statCardIconContent}>
                <div className={classes.statIconBox} style={{ backgroundColor: '#1F3070' }}>
                  <PersonAddIcon style={{ color: '#fff', fontSize: 26 }} />
                </div>
                <div>
                  <Typography className={classes.statValue}>
                    {Number(apiSummary.activeReferrals).toLocaleString()}
                  </Typography>
                  <Typography className={classes.statLabel}>{t('candidateEngagement.metrics.activeReferrals')}</Typography>
                </div>
              </CardContent>
            </Card>
            <Card className={classes.statCard}>
              <CardContent className={classes.statCardIconContent}>
                <div className={classes.statIconBox} style={{ backgroundColor: '#6B4FBB' }}>
                  <PeopleIcon style={{ color: '#fff', fontSize: 26 }} />
                </div>
                <div>
                  <Typography className={classes.statValue}>
                    {Number(apiSummary.applications).toLocaleString()}
                  </Typography>
                  <Typography className={classes.statLabel}>{t('candidateEngagement.metrics.applications')}</Typography>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs
            value={activeTab}
            onChange={(_event, value) => setActiveTab(value)}
            className={classes.tabRoot}
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map(tab => (
              <Tab key={tab.value} label={tab.label} value={tab.value} className={classes.tabCell} />
            ))}
          </Tabs>

          <div className={classes.gridSection}>
            <div className={classes.filtersRow}>
              <div className={classes.filterCell}>
                <CandidateAggregateSearch
                  filterName={t('candidateEngagement.filters.candidate')}
                  name="candidateEngagementCandidate"
                  isMultiSelect
                  value={candidateFilterDraft}
                  onChange={setCandidateFilterDraft}
                  apply={() => setCandidateFilterApplied(candidateFilterDraft)}
                />
              </div>
              <div className={classes.filterCell}>
                <TypeAheadDropdown
                  filterName={t('candidateEngagement.filters.brand')}
                  name="candidateEngagementBrand"
                  isMultiSelect
                  isSelectAll
                  options={brandDropdownOptions}
                  value={brandFilterDraft}
                  onChange={setBrandFilterDraft}
                  apply={() => setBrandFilterApplied(brandFilterDraft)}
                />
              </div>
              <div className={classes.filterCell}>
                <TextField
                  className={classes.leadOriginField}
                  size="small"
                  fullWidth
                  variant="outlined"
                  placeholder={t('candidateEngagement.filters.leadOrigin')}
                  value={leadOriginInput}
                  onChange={e => setLeadOriginInput(e.target.value)}
                  onBlur={commitLeadOriginFilter}
                  name="candidateEngagementLeadOrigin"
                  inputProps={{ 'aria-label': t('candidateEngagement.filters.leadOrigin') }}
                  InputProps={{
                    sx: {
                      color: leadOriginInput.trim() ? '#555555' : '#C0C0C0',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#000000',
                      },
                    },
                  }}
                />
              </div>
              <div className={classes.filterCell}>
                <DateRangePicker
                  variant="outlined"
                  single
                  trailingIconShow
                  secondaryLabel={t('candidateEngagement.filters.inquiryCreatedDate')}
                  initialDateRange={createdDateFilter || undefined}
                  onApply={value => setCreatedDateFilter(value?.startDate || value?.endDate ? value : null)}
                  applyOnClickAway
                  clickAway={false}
                  disablePortal
                  useMaxWidth
                  maxWidth={210}
                />
              </div>
              <div className={classes.filterActionButtons}>
                <SearchPageIcons resetlbutton onResetForm={clearAllFilters} />
              </div>
            </div>

            {(candidateChips.length > 0 || brandFilterApplied.length > 0 || leadOriginChips.length > 0) && (
              <div className={classes.chipsRow}>
                <ChipsContainer
                  chips={allChips}
                  isCollapse
                  maxHeight={38}
                  onClick={() => {
                    return;
                  }}
                  onDelete={(field, chip) => {
                    if (field === 'candidate') {
                      removeCandidateChip(chip);
                      return;
                    }
                    if (field === 'leadOrigin') {
                      setLeadOriginInput('');
                      setLeadOriginApplied('');
                      return;
                    }

                    removeBrandChip(chip?.value);
                  }}
                />
              </div>
            )}

            <div className={classes.tableWrap}>
              {!userPreference?.loaded || displayColumns.length === 0 || !gridInitialState ? (
                <div className="center" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                  <CircularProgress />
                </div>
              ) : (
                <XGrid
                  apiRef={gridApiRef}
                  initialState={gridInitialState}
                  columnVisibilityModel={columnVisibilityModel}
                  defaultColumnVisibilityModel={defaultColumnVisibilityModel}
                  rows={filteredRows}
                  columns={displayColumns}
                  defaultColumns={defaultColumns}
                  gridTag={CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG}
                  title={tabTitle}
                  loading={isLoading}
                  getRowId={row => row.id}
                  disableSelectionOnClick
                  density="compact"
                  sortingMode="server"
                  defaultRowsPerPage={25}
                  rowsPerPageOptions={[25, 50, 100]}
                  onSortModelChange={model => {
                    const next = model?.[0];
                    setSortColumn(next?.field || 'createdDate');
                    setSortDirection(next?.sort === 'asc' ? 'ASC' : 'DESC');
                  }}
                  onColumnResizeStop={_columns => {
                    savePreferences({
                      columns: _columns,
                      visibilityModel: _.cloneDeep(
                        columnVisibilityModel ?? defaultColumnVisibilityModel,
                      ),
                    });
                  }}
                  onColumnOrderChange={(_params, _event, _details, _cols) => {
                    savePreferences({
                      columns: _cols,
                      visibilityModel: _.cloneDeep(
                        columnVisibilityModel ?? defaultColumnVisibilityModel,
                      ),
                    });
                  }}
                  onColumnVisibilityModelChange={model => {
                    dispatch(gridStateActions.setColumnVisibilityModel(model));
                    savePreferences({
                      columns: _.cloneDeep(gridApiRef.current?.getAllColumns() ?? displayColumns),
                      visibilityModel: _.cloneDeep(model),
                    });
                    const updatedColumns = _.cloneDeep(displayColumns).map(column => ({
                      ...column,
                      hide: model[column.field] === false,
                    }));
                    if (!_.isEqual(updatedColumns, displayColumns)) {
                      setDisplayColumns(updatedColumns);
                    }
                  }}
                  toolbar
                  toolbarProps={{
                    columnSelector: true,
                    refreshButton: true,
                    refreshGrid: () => {
                      setReloadToken(prev => prev + 1);
                    },
                    resetColumns: () => {
                      determineGridColumns(true);
                      dispatch(gridStateActions.setColumnVisibilityModel(defaultColumnVisibilityModel));
                      dispatch(
                        userDevicePreferenceActions.saveUserGridPreferenceAction({
                          id: CANDIDATE_ENGAGEMENT_INQUIRIES_GRID_TAG,
                          value: {
                            ...(gridPreferences?.value || {}),
                            columns: new GridColumns(defaultColumns as XGridColumns).columns,
                            columnVisibilityModel: defaultColumnVisibilityModel,
                          },
                        }),
                      );
                      dispatch(
                        globalActions.setSnackBar({
                          message: t('global.xgrid.columnsResetMessage'),
                          severity: 'success',
                        }),
                      );
                      setGridInitialState({
                        columns: { columnVisibilityModel: defaultColumnVisibilityModel },
                      });
                    },
                  }}
                />
              )}
            </div>
          </div>

          <DuplicateFilterAlertModal
            isAlertModalOpen={isAlertModalOpen}
            setAlertModalOpen={setAlertModalOpen}
            setMangeFilters={() => undefined}
            existingFilterSet={existingFilterSet}
          />

          <SaveModal
            isSaveModalOpen={isSaveModalOpen}
            setSaveModalOpen={setSaveModalOpen}
            updateExistingFilter={updateExistingFilter}
          />

          <SaveAsModal
            isSaveAsModalOpen={isSaveAsModalOpen}
            setSaveAsModalOpen={setSaveAsModalOpen}
            disableSaveButton={disableSaveButton}
            handleFilterNameChange={handleFilterNameChange}
            addNewFilter={addNewFilter}
            errorText={saveAsErrorText}
            chips={null}
            setDisableSaveButton={setDisableSaveButton}
          />
        </div>
      }
    />
  );
};
